import { useEffect, useState, useCallback } from "react"
import { supabase } from "@/lib/supabaseClient"
import { useAuth } from "@/features/auth/auth-context"

type Profile = {
  id: string
  display_name: string | null
  email: string | null
  instagram_handle: string | null
}

export const clearDisplayName = async (userId: string) => {
  const { error } = await supabase
    .from("profiles")
    .update({ display_name: null })
    .eq("id", userId)

  if (error) console.error("Error clearing display name:", error.message)
}

export function useProducerProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!user) {
      setProfile(null)
      setLoading(false)
      return
    }
    setLoading(true)
    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, email, instagram_handle")
      .eq("id", user.id)
      .single()

    if (error && error.code !== "PGRST116") {
      console.error("Error fetching profile:", error.message)
      setError(error.message)
      setProfile(prev => prev ?? { id: user.id, display_name: null, email: null, instagram_handle: null })
    } else {
      setProfile(
        (data as Profile) ??
          { id: user.id, display_name: null, email: null, instagram_handle: null }
      )
    }
    setLoading(false)
  }, [user])

  useEffect(() => {
    refresh()
  }, [refresh])

  const setDisplayName = useCallback(async (name: string) => {
    if (!user) return
    const { error } = await supabase
      .from("profiles")
      .upsert({ id: user.id, display_name: name })

    if (error) {
      console.error("Error saving display name:", error.message)
      setError(error.message)
    } else {
      setProfile(prev => ({
        ...(prev ?? { id: user.id, display_name: null, email: null, instagram_handle: null }),
        display_name: name,
      }))
    }
  }, [user])

  const updateContacts = useCallback(
    async ({ email, instagram_handle }: { email: string | null; instagram_handle: string | null }) => {
      if (!user) return { error: "No user" as const }

      const cleanEmail = email?.trim() || null
      const cleanIg = instagram_handle?.replace(/^@/, "") || null

      const { error } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          display_name: profile?.display_name ?? null,
          email: cleanEmail,
          instagram_handle: cleanIg,
        })

      if (error) {
        console.error("Error saving contacts:", error.message)
        setError(error.message)
        return { error }
      }

      // Update local cache immediately so gating & renders use new values
      setProfile(prev => ({
        ...(prev ?? { id: user.id, display_name: null, email: null, instagram_handle: null }),
        email: cleanEmail,
        instagram_handle: cleanIg,
      }))

      return { error: null as const }
    },
    [user, profile?.display_name]
  )

  const hasContact = Boolean(
    (profile?.email && profile.email.trim()) ||
    (profile?.instagram_handle && profile.instagram_handle.trim())
  )

  return {
    // data
    displayName: profile?.display_name ?? null,
    email: profile?.email ?? null,
    instagram_handle: profile?.instagram_handle ?? null,
    hasContact,

    // states
    loading,
    error,

    // actions
    setDisplayName,
    updateContacts,
    refresh,
  }
}
